import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../../firebase';
import { ArrowLongLeftIcon, ArrowLongRightIcon } from '@heroicons/react/20/solid';
import logger from '../../../utils/logger';
import AddFeedback from './AddFeedback';
import FeedbackDetails from './FeedBackDetails';
import Modal from 'react-modal';


Modal.setAppElement('#root');

const AllFeedback = () => {
    const [feedbackList, setFeedbackList] = useState([]);
    const [sortField, setSortField] = useState('date'); 
    const [isLoading, setIsLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10; 
    const totalPages = Math.ceil(feedbackList.length / itemsPerPage);
    const paginate = (pageNumber) => setCurrentPage(pageNumber);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const [isAddFeedbackModalOpen, setIsAddFeedbackModalOpen] = useState(false);
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const [selectedFeedback, setSelectedFeedback] = useState(null);

    const pageNumbers = [];
    for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
    }


    const handleDetails = (feedback) => {
        setSelectedFeedback(feedback);
        setIsDetailsModalOpen(true);
      };

    useEffect(() => {
        const fetchFeedback = async () => {
            setIsLoading(true);
            try {
                const q = query(collection(db, "feedbacks"), orderBy(sortField));
                const querySnapshot = await getDocs(q);
                const feedbacks = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setFeedbackList(feedbacks);
            } catch (error) {
                logger.error("Error fetching feedback: ", error);
            }
            setIsLoading(false);
        };

        fetchFeedback();
    }, [sortField]);

    const handleSortChange = (e) => {
        setSortField(e.target.value);
    };

    
    const handleNewFeedback = () => {
        setIsAddFeedbackModalOpen(true);
    }

    const handleClose = () => setIsAddFeedbackModalOpen(false);

    useEffect(() => {
        const fetchCustomersAndFeedback = async () => {
            setIsLoading(true);
            try {
                // Fetch customers
                const customerSnapshot = await getDocs(collection(db, "customers"));
                const customers = customerSnapshot.docs.reduce((acc, doc) => ({
                    ...acc,
                    [doc.id]: { ...doc.data() }
                }), {});
                
                // Fetch feedback
                const q = query(collection(db, "feedbacks"), orderBy(sortField));
                const feedbackSnapshot = await getDocs(q);
                const feedbacksWithCustomerName = feedbackSnapshot.docs.map(doc => {
                    const feedbackData = doc.data();
                    const customerData = customers[feedbackData.customerId];
                    // Combine feedback with customer name
                    return {
                        ...feedbackData,
                        customerName: customerData ? `${customerData.firstName} ${customerData.lastName}` : "Unknown",
                        id: doc.id
                    };
                });
                setFeedbackList(feedbacksWithCustomerName);
            } catch (error) {
                logger.error("Error fetching data: ", error);
            } finally {
                setIsLoading(false);
            }
        };
    
        fetchCustomersAndFeedback();
    }, [sortField]);
    

    if (isLoading) {
        return (
            <div className="bg-white rounded-2xl shadow-lg p-12">
                <div className="flex flex-col items-center justify-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600"></div>
                    <p className="mt-4 text-gray-600 font-medium">Loading feedback...</p>
                </div>
            </div>
        );
    }


    return (
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            {/* Header Section */}
            <div className="bg-gradient-to-r from-indigo-600 to-blue-600 px-6 py-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex items-center">
                        <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center mr-3">
                            <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                            </svg>
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-white">All Feedback</h2>
                            <p className="text-indigo-100 text-sm">{feedbackList.length} total entries</p>
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3">
                        <div className="relative">
                            <label htmlFor="sortField" className="sr-only">Sort By</label>
                            <select
                                onChange={handleSortChange}
                                value={sortField}
                                className="appearance-none bg-white/10 backdrop-blur-sm text-white border border-white/20 rounded-xl px-4 py-2.5 pr-10 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-white/50 transition-all duration-200"
                            >
                                <option value="date" className="text-gray-900">Sort by Date</option>
                                <option value="status" className="text-gray-900">Sort by Status</option>
                                <option value="customerName" className="text-gray-900">Sort by Customer</option>
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-white">
                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </div>
                        </div>

                        <button
                            type='button'
                            onClick={handleNewFeedback}
                            className="bg-white text-indigo-600 hover:bg-indigo-50 font-semibold px-5 py-2.5 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 transform hover:-translate-y-0.5 flex items-center justify-center"
                        >
                            <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            Add Feedback
                        </button>
                    </div>
                </div>
            </div>

            {/* Table Section */}
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                                Customer Name
                            </th>
                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                                Feedback Type
                            </th>
                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                                Status
                            </th>
                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                                Date
                            </th>
                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {feedbackList.slice(indexOfFirstItem, indexOfLastItem).map(feedback => (
                            <tr key={feedback.id} className="hover:bg-gray-50 transition-colors duration-150">
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm font-medium text-gray-900">{feedback.customerName}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                                        feedback.feedbackType === "Complaint"
                                            ? "bg-red-100 text-red-800"
                                            : feedback.feedbackType === "Commendation"
                                            ? "bg-green-100 text-green-800"
                                            : "bg-gray-100 text-gray-800"
                                    }`}>
                                        {feedback.feedbackType === "Complaint" && (
                                            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                            </svg>
                                        )}
                                        {feedback.feedbackType === "Commendation" && (
                                            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                            </svg>
                                        )}
                                        {feedback.feedbackType}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                                        feedback.status === "Open"
                                            ? "bg-yellow-100 text-yellow-800"
                                            : feedback.status === "Resolved"
                                            ? "bg-green-100 text-green-800"
                                            : "bg-blue-100 text-blue-800"
                                    }`}>
                                        <span className={`w-2 h-2 rounded-full mr-2 ${
                                            feedback.status === "Open"
                                                ? "bg-yellow-500"
                                                : feedback.status === "Resolved"
                                                ? "bg-green-500"
                                                : "bg-blue-500"
                                        }`}></span>
                                        {feedback.status}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                    {feedback.date.toDate().toLocaleDateString('en-US', {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric'
                                    })}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <button
                                        type='button'
                                        onClick={() => handleDetails(feedback)}
                                        className="bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white font-semibold px-4 py-2 rounded-lg text-xs shadow-md hover:shadow-lg transition-all duration-200 transform hover:-translate-y-0.5"
                                    >
                                        View Details
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                <nav className="flex items-center justify-between">
                    <div className="flex-1 flex justify-between sm:hidden">
                        <button
                            onClick={() => paginate(currentPage - 1)}
                            disabled={currentPage === 1}
                            className="relative inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                        >
                            Previous
                        </button>
                        <button
                            onClick={() => paginate(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className="relative inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                        >
                            Next
                        </button>
                    </div>
                    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                        <div>
                            <p className="text-sm text-gray-700">
                                Showing <span className="font-semibold">{indexOfFirstItem + 1}</span> to{' '}
                                <span className="font-semibold">{Math.min(indexOfLastItem, feedbackList.length)}</span> of{' '}
                                <span className="font-semibold">{feedbackList.length}</span> results
                            </p>
                        </div>
                        <div>
                            <nav className="relative z-0 inline-flex rounded-lg shadow-sm -space-x-px" aria-label="Pagination">
                                <button
                                    onClick={() => paginate(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="relative inline-flex items-center px-3 py-2 rounded-l-lg bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                                >
                                    <ArrowLongLeftIcon className="h-5 w-5" aria-hidden="true" />
                                    <span className="ml-2">Previous</span>
                                </button>
                                <div className="hidden md:flex">
                                    {pageNumbers.map(number => (
                                        <button
                                            key={number}
                                            onClick={() => paginate(number)}
                                            className={`relative inline-flex items-center px-4 py-2 text-sm font-medium transition-all duration-200 ${
                                                currentPage === number
                                                    ? 'z-10 bg-indigo-600 text-white'
                                                    : 'bg-white text-gray-700 hover:bg-gray-50'
                                            }`}
                                        >
                                            {number}
                                        </button>
                                    ))}
                                </div>
                                <button
                                    onClick={() => paginate(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className="relative inline-flex items-center px-3 py-2 rounded-r-lg bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                                >
                                    <span className="mr-2">Next</span>
                                    <ArrowLongRightIcon className="h-5 w-5" aria-hidden="true" />
                                </button>
                            </nav>
                        </div>
                    </div>
                </nav>
            </div>

            {/* Modals */}
            {isAddFeedbackModalOpen && (
                <Modal
                    isOpen={isAddFeedbackModalOpen}
                    onRequestClose={handleClose}
                    className="relative bg-white rounded-2xl shadow-2xl max-w-4xl mx-auto my-8 outline-none"
                    overlayClassName="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
                >
                    <AddFeedback onRequestClose={handleClose} />
                </Modal>
            )}

            {isDetailsModalOpen && selectedFeedback && (
                <FeedbackDetails
                    isOpen={isDetailsModalOpen}
                    onRequestClose={() => setIsDetailsModalOpen(false)}
                    feedback={selectedFeedback}
                    customerName={selectedFeedback.customerName}
                />
            )}
        </div>
    );
};

export default AllFeedback;